
import React, { useState } from 'react';
import { FileText, Briefcase, Users, Settings } from 'lucide-react';

export interface ReportTemplate {
  id: string;
  name: string;
  description: string;
  icon: React.ComponentType<any>;
  preview: string;
  styles: {
    primaryColor: string;
    accentColor: string;
    fontFamily: string;
    headerStyle: string;
  };
}

interface ReportTemplateSelectorProps {
  onSelectTemplate: (template: ReportTemplate) => void;
  onClose: () => void;
}

const templates: ReportTemplate[] = [
  {
    id: 'executive',
    name: 'Executive Summary',
    description: 'Clean, professional layout for executive briefings',
    icon: Briefcase,
    preview: 'Modern layout with executive summary focus, data highlights, and strategic recommendations',
    styles: {
      primaryColor: '#1e40af',
      accentColor: '#3b82f6',
      fontFamily: 'Inter, sans-serif',
      headerStyle: 'minimal'
    }
  },
  {
    id: 'campaign',
    name: 'Campaign Strategy',
    description: 'Comprehensive format for political campaign analysis',
    icon: Users,
    preview: 'Detailed sections for stakeholder analysis, strategic frameworks, and implementation roadmaps',
    styles: {
      primaryColor: '#dc2626',
      accentColor: '#ef4444',
      fontFamily: 'system-ui, sans-serif',
      headerStyle: 'bold'
    }
  },
  {
    id: 'consulting',
    name: 'Consulting Report',
    description: 'Professional consulting format with clear recommendations',
    icon: FileText,
    preview: 'Structured layout with methodology, findings, and actionable recommendations',
    styles: {
      primaryColor: '#059669',
      accentColor: '#10b981',
      fontFamily: 'Georgia, serif',
      headerStyle: 'traditional'
    }
  }
];

const ReportTemplateSelector: React.FC<ReportTemplateSelectorProps> = ({ onSelectTemplate, onClose }) => {
  const [selectedTemplate, setSelectedTemplate] = useState<ReportTemplate | null>(null);

  const handleSelect = () => {
    if (selectedTemplate) {
      onSelectTemplate(selectedTemplate);
      onClose();
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col">
        <div className="flex justify-between items-center p-6 border-b">
          <div>
            <h2 className="text-2xl font-semibold text-primary-800">Choose Report Template</h2>
            <p className="text-neutral-600 mt-1">Select a professional template for your report formatting</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-neutral-500 hover:text-neutral-700 transition-colors"
          >
            ✕
          </button>
        </div>
        
        <div className="p-6 flex-grow overflow-y-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {templates.map((template) => {
              const IconComponent = template.icon;
              return (
                <div
                  key={template.id}
                  className={`border-2 rounded-lg p-6 cursor-pointer transition-all hover:shadow-lg ${
                    selectedTemplate?.id === template.id 
                      ? 'border-primary-500 bg-primary-50' 
                      : 'border-neutral-200 hover:border-primary-300'
                  }`}
                  onClick={() => setSelectedTemplate(template)}
                >
                  <div className="flex items-center mb-4">
                    <IconComponent className="h-8 w-8 mr-3" style={{ color: template.styles.primaryColor }} />
                    <h3 className="text-lg font-semibold text-neutral-800">{template.name}</h3>
                  </div>
                  <p className="text-sm text-neutral-600 mb-4">{template.description}</p>
                  <div className="bg-neutral-100 rounded p-3 text-xs text-neutral-700">
                    {template.preview}
                  </div>
                  <div className="mt-4 flex items-center space-x-2">
                    <div 
                      className="w-4 h-4 rounded-full" 
                      style={{ backgroundColor: template.styles.primaryColor }}
                    />
                    <div 
                      className="w-4 h-4 rounded-full" 
                      style={{ backgroundColor: template.styles.accentColor }}
                    />
                    <span className="text-xs text-neutral-500 ml-2">{template.styles.fontFamily.split(',')[0]}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
        
        <div className="flex justify-between items-center p-6 border-t bg-neutral-50">
          <div className="flex items-center text-sm text-neutral-600">
            <Settings className="h-4 w-4 mr-2" />
            Additional customization options available after selection
          </div>
          <div className="flex space-x-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-neutral-600 hover:text-neutral-800 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSelect}
              disabled={!selectedTemplate}
              className="px-6 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Use Template
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReportTemplateSelector;
